import React, { useState } from 'react';
import { faqItems } from '../data/mock';
import blogArticles from '../data/blogArticles';
import { GUIDE_FAQ } from '../data/guideTechnique';
import { ChevronDown, Search, MessageCircle, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import SizeCalculator from '../components/SizeCalculator';

// Questions issues des guides du blog, rattachées à leur article source
const guideQuestions = blogArticles
  .flatMap((article) =>
    article.faq.map((f) => ({
      question: f.question,
      answer: f.answer,
      articleSlug: article.slug,
      articleTitle: article.title,
    }))
  )
  .concat(
    GUIDE_FAQ.map((f) => ({
      question: f.question,
      answer: f.answer,
      articleSlug: 'guide-technique',
      articleTitle: 'Guide technique & sécurité des barnums',
    }))
  );

const allQuestions = faqItems
  .map((item) => ({ question: item.question, answer: item.answer, articleSlug: null }))
  .concat(guideQuestions);

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: allQuestions.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const FaqAccordion = ({ items, openQuestion, onToggle }) => (
  <div className="space-y-4">
    {items.map((item) => (
      <div key={item.question} className="bg-white rounded-xl overflow-hidden shadow-sm">
        <button
          onClick={() => onToggle(item.question)}
          className="w-full flex items-center justify-between p-6 text-left"
          data-testid={'faq-question-' + item.question.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}
        >
          <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
          <ChevronDown
            className={
              'w-5 h-5 text-orange-500 flex-shrink-0 transition-transform ' +
              (openQuestion === item.question ? 'rotate-180' : '')
            }
          />
        </button>
        {openQuestion === item.question && (
          <div className="px-6 pb-6">
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{item.answer}</p>
            {item.articleSlug && (
              <Link
                to={item.articleSlug === 'guide-technique' ? '/guide-technique' : '/blog/' + item.articleSlug}
                className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-orange-600 hover:underline"
                data-testid={'faq-guide-link-' + item.articleSlug}
              >
                <BookOpen className="w-4 h-4" /> Lire le guide : {item.articleTitle}
              </Link>
            )}
          </div>
        )}
      </div>
    ))}
  </div>
);

const FAQPage = () => {
  const [openQuestion, setOpenQuestion] = useState(faqItems[0]?.question || null);
  const [searchQuery, setSearchQuery] = useState('');

  const matches = (item) =>
    item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredFaq = allQuestions.filter((item) => !item.articleSlug && matches(item));
  const filteredGuides = allQuestions.filter((item) => item.articleSlug && matches(item));
  const toggle = (q) => setOpenQuestion(openQuestion === q ? null : q);

  return (
    <div className="min-h-screen bg-gray-50 pt-24" data-testid="faq-page">
      <JsonLd data={faqJsonLd} />
      {/* Header */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Foire Aux Questions</h1>
          <p className="text-base md:text-lg text-orange-100 max-w-2xl mx-auto">
            Trouvez rapidement les réponses à vos questions les plus fréquentes
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une question..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="faq-search-input"
            className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-lg"
          />
        </div>

        <FaqAccordion items={filteredFaq} openQuestion={openQuestion} onToggle={toggle} />

        {filteredFaq.length === 0 && filteredGuides.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucune question trouvée pour "{searchQuery}"</p>
          </div>
        )}

        {/* Questions issues de nos guides */}
        {filteredGuides.length > 0 && (
          <div className="mt-14" data-testid="faq-guides-section">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Dimensions, prix et choix de structure
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Les questions les plus posées, extraites de nos guides pratiques.
            </p>
            <FaqAccordion items={filteredGuides} openQuestion={openQuestion} onToggle={toggle} />
          </div>
        )}

        {/* Calculateur de taille */}
        <SizeCalculator />

        {/* Contact CTA */}
        <div className="mt-12 bg-orange-100 rounded-2xl p-8 text-center">
          <MessageCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2">
            Vous n'avez pas trouvé votre réponse ?
          </h2>
          <p className="text-gray-600 mb-6">
            Notre équipe est là pour répondre à toutes vos questions
          </p>
          <Link
            to="/devis"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-medium rounded-full hover:bg-orange-600 transition-colors"
            data-testid="faq-cta-devis"
          >
            Contactez-nous
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
